/* global COMMENTS_VERSION */
import React, { useEffect, useRef, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { modal } from 'react-redux-modal';
import Collapsible from 'react-collapsible';
import queryString from 'query-string';
import { CommentsLibrary } from '@exploreorg/explore-comments';
import { LoginModalView } from '../../components/Modals';
import './styles.scss';
import { commentsConfig } from '../../utils';
import CommentsCollapsibleHeaderTitle from './CommentsCollapsibleHeaderTitle';
import CommentsCTAButtons from './CommentsCTAButtons';
import { Loader } from '../index';

const defaultProps = {
  partner: null,
  commentToken: null,
  compactView: false,
};

const propTypes = {
  livecamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  pageUrl: PropTypes.string.isRequired,
  partner: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    has_email: PropTypes.bool,
  }),
  commentToken: PropTypes.string,
  /**
   * If true, only the module will be rendered that we can use in the multi-boards view
   */
  compactView: PropTypes.bool,
};

const ScrollToComments = ({ scrollToComments }) => {
  const ref = useRef(null);

  const scrollToElement = () => {
    // eslint-disable-next-line chai-friendly/no-unused-expressions
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (scrollToComments) {
      setTimeout(scrollToElement, 3000);
    }
  }, [scrollToComments]);

  return (
    <div ref={ref} />
  );
};

ScrollToComments.propTypes = {
  scrollToComments: PropTypes.bool,
};

ScrollToComments.defaultProps = {
  scrollToComments: false,
};

const conf = commentsConfig();

const CommentsView = ({
  partner,
  commentToken,
  livecamId,
  pageUrl,
  compactView,
}) => {
  const [commentId, setCommentId] = useState();
  const [loadComments, setLoadComments] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = queryString.parse(window.location.search);
    const { commentId: urlCommentId } = query;
    setCommentId(urlCommentId);
    setLoadComments(true);
  }, []);

  const clearCommentId = () => {
    const params = new URLSearchParams(window.location.search);
    const commentIdQueryParam = params.get('commentId');

    if (commentIdQueryParam) {
      params.delete('commentId');
      const stringifiedParams = params.toString();

      const newUrl = `${window.location.pathname}${
        stringifiedParams ? `?${stringifiedParams}` : ''
      }`;
      window.history.pushState({}, document.title, newUrl);
      setCommentId({ commentId: null });
    }
  };

  const refreshComments = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  };

  useEffect(() => {
    if (commentId) {
      clearCommentId();
    }
  }, [livecamId]);

  const openLogin = (e) => {
    e.preventDefault();
    modal.add(LoginModalView, { title: '', size: 'medium', hideTitleBar: true, id: 'login' });
  };

  const commentModuleParameters = {
    theme: 'light',
    token: commentToken,
    livecamId: String(livecamId),
    commentId,
  };

  let versionToDisplay = 'unknown';
  try {
    versionToDisplay = `${COMMENTS_VERSION}`;
  } catch (error) {
    console.log('Cannot get version of application.');
  }

  if (compactView) {
    return (
      <Fragment>
        <ScrollToComments scrollToComments={!!commentId} />
        <div className="d-flex flex-wrap justify-content-end mb-1 mx-3">
          <button className="btn btn-secondary btn-sm d-inline-block" onClick={refreshComments}>
            <span className="icon font-weight-bold" style={{ fontSize: '1.35em' }}>⟳</span> Refresh comments
          </button>
        </div>
        <div className="disqus_shorten">
          <div className="pl-3 pr-3 pb-3 pt-1">
            {loading && (
            <div className="position-relative" style={{ height: '200px' }}><Loader fill dark style={{ height: 200 }} />
            </div>)}
            {loadComments && (!!livecamId) && (!loading) && (
            <CommentsLibrary
              config={conf}
              {...commentModuleParameters}
              removeCommentIdFromUrlParams={clearCommentId}
              onLoginClick={openLogin}
              pageUrl={pageUrl}
            />
            )}
            <div className="small text-center text-black-50">{`Comments Version: ${versionToDisplay}`}</div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Collapsible
      trigger={(<CommentsCollapsibleHeaderTitle />)}
      open
      triggerClassName="Collapsible__trigger container-rounded text-uppercase u-font-foundation-roman u-textSize-20 pY-15"
      triggerOpenedClassName="Collapsible__trigger is-open container-top-rounded text-uppercase u-font-foundation-roman u-textSize-20 pY-15"
      contentOuterClassName="info-box-content u-font-foundation-roman u-textSize-18"
      contentInnerClassName="disqus-height"
    >
      <ScrollToComments scrollToComments={!!commentId} />
      <CommentsCTAButtons partner={partner} livecamId={livecamId} />
      <div className="d-flex flex-wrap justify-content-end mb-1 mx-3">
        <button className="btn btn-secondary btn-sm d-inline-block" onClick={refreshComments}>
          <span className="icon font-weight-bold" style={{ fontSize: '1.35em' }}>⟳</span> Refresh comments
        </button>
      </div>
      <div className="disqus_shorten">
        <div className="pl-3 pr-3 pb-3 pt-1">
          {loading && (<div className="position-relative" style={{ height: '200px' }}> <Loader fill dark style={{ height: 200 }} /> </div>)}
          {loadComments && (!!livecamId) && (!loading) && (
            <CommentsLibrary
              config={conf}
              {...commentModuleParameters}
              removeCommentIdFromUrlParams={clearCommentId}
              onLoginClick={openLogin}
              pageUrl={pageUrl}
            />
          )}
          <div className="small text-center text-black-50">{`Comments Version: ${versionToDisplay}`}</div>
        </div>
      </div>
    </Collapsible>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    partner: state.currentItem.data.partner,
    commentToken: state.auth.commentToken,
    livecamId: ownProps.livecamId || state.currentItem.data.id,
    pageUrl: ownProps.pageUrl || state.currentItem.data.canonical_url,
  };
};

CommentsView.propTypes = propTypes;
CommentsView.defaultProps = defaultProps;

export default connect(mapStateToProps)(CommentsView);
